import { analyticsMaxDate } from '@/lib/analyticsDateRange';
import { useState } from 'react';
import {
  DateRangePicker,
  DateRangePickerPresetKey,
  buildDateRangePickerPresets,
  cn,
  type DateRangePickerValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { DashboardComparisonToggle } from '@/components/dashboard/DashboardComparisonToggle';
import {
  PerformanceAnalyzerTab,
  previousPerformanceRange,
  type PerformanceAnalyzerRange,
} from '../../model';
import { PublisherDemandPerformance } from '../PublisherDemandPerformance';
import { StitcherPerformance } from '../StitcherPerformance';

const presets = buildDateRangePickerPresets(
  [
    DateRangePickerPresetKey.Today,
    DateRangePickerPresetKey.Yesterday,
    DateRangePickerPresetKey.ThisWeek,
    DateRangePickerPresetKey.Last7Days,
    DateRangePickerPresetKey.ThisMonth,
    DateRangePickerPresetKey.LastMonth,
    DateRangePickerPresetKey.Last30Days,
    DateRangePickerPresetKey.Last3Months,
  ],
  { endPeriodsYesterday: true },
);

export function PerformanceAnalyzerPage({
  primaryRange,
  onPrimaryRangeChange,
}: {
  primaryRange: DateRangePickerValue;
  onPrimaryRangeChange: (range: DateRangePickerValue) => void;
}) {
  const [tab, setTab] = useState(PerformanceAnalyzerTab.PublisherDemand);
  const [comparison, setComparison] = useState<DateRangePickerValue | null>(null);
  const range: PerformanceAnalyzerRange = { primary: primaryRange, comparison };

  function changeTab(next: PerformanceAnalyzerTab) {
    setTab(next);
    setComparison(null);
  }

  return (
    <main className="min-w-0 flex-1">
      {/* Tabs and range controls share one bar; on narrow screens the controls sit above the tabs
          so the active tab underline stays on the bar's bottom edge. */}
      <div className="border-border bg-card page-gutter flex min-w-0 flex-col gap-3 border-b pt-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:pt-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2 lg:order-last lg:shrink-0 lg:flex-nowrap lg:justify-end">
          <DashboardComparisonToggle
            checked={comparison !== null}
            onCheckedChange={(checked) => {
              setComparison(checked ? previousPerformanceRange(primaryRange) : null);
            }}
          />
          <DateRangePicker
            label={dashboardT('performanceAnalyzer.dateRange')}
            triggerClassName="h-8"
            presets={presets}
            value={primaryRange}
            maxDate={analyticsMaxDate()}
            onChange={(value) => {
              onPrimaryRangeChange(value);
              if (comparison) setComparison(previousPerformanceRange(value));
            }}
          />
          {comparison ? (
            <DateRangePicker
              label={dashboardT('performanceAnalyzer.comparisonRange')}
              triggerClassName="h-8"
              presets={presets}
              value={comparison}
              maxDate={analyticsMaxDate()}
              onChange={setComparison}
            />
          ) : null}
        </div>
        <nav aria-label={dashboardT('performanceAnalyzer.tabsLabel')} className="min-w-0 lg:flex-1">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((item) => (
              <button
                key={item.value}
                type="button"
                aria-current={tab === item.value ? 'page' : undefined}
                className={cn(
                  'text-muted-foreground cursor-pointer border-b-2 border-transparent px-3 py-3 text-sm font-medium whitespace-nowrap transition-colors',
                  tab === item.value && 'shigo-supply-tab--active',
                )}
                onClick={() => {
                  changeTab(item.value);
                }}
              >
                {dashboardT(item.label)}
              </button>
            ))}
          </div>
        </nav>
      </div>
      <div className="page-gutter min-w-0 space-y-4 py-4 sm:py-6 lg:py-8">
        {tab === PerformanceAnalyzerTab.PublisherDemand ? (
          <PublisherDemandPerformance range={range} />
        ) : (
          <StitcherPerformance range={range} />
        )}
      </div>
    </main>
  );
}

const tabs = [
  {
    value: PerformanceAnalyzerTab.PublisherDemand,
    label: 'performanceAnalyzer.tabs.publisherDemand' as const,
  },
  { value: PerformanceAnalyzerTab.Stitcher, label: 'performanceAnalyzer.tabs.stitcher' as const },
];
